import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const EditWorkoutModal = ({ isOpen, onClose, workout, existingTypes, onConfirm }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: '',
        distance_km: 0,
        duration_min: 0,
        altitude_gain_m: 0
    });

    useEffect(() => {
        if (workout) {
            setFormData({
                title: workout.title || '',
                description: workout.description || '',
                type: workout.type || 'run',
                distance_km: workout.distance_km || 0,
                duration_min: workout.duration_min || 0,
                altitude_gain_m: workout.altitude_gain_m || 0
            });
        }
    }, [workout]);

    if (!isOpen || !workout) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm(formData);
    };

    return (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-100">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white/95 backdrop-blur-md z-10">
                    <h3 className="text-xl font-black text-gray-900 tracking-tight">{t('workout.edit_workout')}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors">✕</button>
                </div>

                <div className="p-6">
                    <div className="mb-6 bg-indigo-50/50 border border-indigo-100 p-4 rounded-2xl flex justify-between items-center text-sm">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wide mb-0.5">{t('workout.day')}</span>
                            <span className="font-bold text-indigo-900 text-base">{workout.day}</span>
                        </div>
                        <div className="flex flex-col text-right">
                            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wide mb-0.5">{t('workout.date')}</span>
                            <span className="font-mono text-indigo-900 font-bold">{workout.date || 'N/A'}</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{t('workout.type')}</label>
                            <div className="relative">
                                <select
                                    className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium appearance-none"
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                >
                                    {existingTypes.map(type => (
                                        <option key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{t('workout.title')}</label>
                            <input
                                type="text"
                                className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium placeholder:text-gray-400"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{t('workout.description')}</label>
                            <textarea
                                className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium placeholder:text-gray-400 h-32 resize-none"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                required
                            />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2 truncate">{t('workout.distance')} (km)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    className="block w-full border border-gray-200 rounded-xl px-3 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium text-center"
                                    value={formData.distance_km}
                                    onChange={(e) => setFormData({ ...formData, distance_km: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2 truncate">{t('workout.duration')} (min)</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="block w-full border border-gray-200 rounded-xl px-3 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium text-center"
                                    value={formData.duration_min}
                                    onChange={(e) => setFormData({ ...formData, duration_min: parseInt(e.target.value) || 0 })}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2 truncate">{t('workout.elevation')} (m)</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="block w-full border border-gray-200 rounded-xl px-3 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium text-center"
                                    value={formData.altitude_gain_m}
                                    onChange={(e) => setFormData({ ...formData, altitude_gain_m: parseInt(e.target.value) || 0 })}
                                />
                            </div>
                        </div>

                        <div className="pt-6 flex justify-end gap-3 border-t border-gray-50 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-full transition font-bold text-sm uppercase tracking-wide"
                            >
                                {t('workout.cancel')}
                            </button>
                            <button
                                type="submit"
                                className="px-6 py-2.5 bg-indigo-600 text-white rounded-full font-bold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:bg-indigo-700 transition-all duration-200 text-sm uppercase tracking-wide"
                            >
                                {t('workout.save_changes')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditWorkoutModal;

